import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import React from "react";
import { IconAlertCircle, IconCheck, IconCopy, IconRefresh, IconSparkles } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation } from "../../../../../../../openapi";
import { useAppSelector } from "../../../../../../hooks";
import { useGetFilesFromAzureBlobStorageQuery } from "../../../../../azureBlobStorageRTK";
import {
  selectBreeamIssueFilesQueryArgs,
  selectBreeamGuidanceTextSource,
  selectBreeamModalHeader,
  selectBreeamPrerequisites,
  selectBreeamRunArgs,
  selectCopyableAiAssessmentText,
  selectEffectiveVerdict,
  selectParsedAiAssessment,
  selectTechnicalAnalysis,
  selectVerdictColor,
} from "./IssueBreeamCriterionCheckModal.selectors";

const getErrorMessage = (error: unknown) => {
  if (error == null) return "Ukjent feil";
  if (typeof error === "string") return error;
  if (typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string") return data;
    return JSON.stringify(data);
  }
  return JSON.stringify(error);
};

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card withBorder radius="md" padding="md">
    <Stack gap="sm">
      <Text fw={600}>{title}</Text>
      {children}
    </Stack>
  </Card>
);

type TBreeamOutputLanguage = "NO" | "EN";

const formatNumber = (value: number) => new Intl.NumberFormat("nb-NO").format(value);

const formatNok = (value: number) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const IssueBreeamCriterionCheckModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const hasLoadedInputPreviewRef = useRef(false);
  const [showTechnicalDebug, setShowTechnicalDebug] = useState(false);
  const [showInputData, setShowInputData] = useState(true);
  const [outputLanguage, setOutputLanguage] = useState<TBreeamOutputLanguage>("NO");
  const [
    runBreeamCriterionCheck,
    { data: result = null, error: runError, isLoading: isRunning, reset: resetBreeamCriterionCheck },
  ] = usePostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckMutation();
  const issueFilesQueryArgs = useAppSelector(selectBreeamIssueFilesQueryArgs);
  useGetFilesFromAzureBlobStorageQuery(issueFilesQueryArgs ?? { projectId: "", path: "" }, {
    skip: issueFilesQueryArgs == null,
  });
  const breeamRunArgs = useAppSelector(selectBreeamRunArgs);
  const { issueId, issueTitle } = useAppSelector(selectBreeamModalHeader);
  const guidanceTextSource = useAppSelector(selectBreeamGuidanceTextSource);

  const { canRunCheck, isCheckingPrerequisites, missingPrerequisites } = useAppSelector(selectBreeamPrerequisites);
  const canStartCheck = canRunCheck && breeamRunArgs != null;
  const hasAiResult = result?.status === "success";
  const hasInputPreview = result?.inputData.aiInput != null;
  const isLoadingInputPreview = isRunning && !hasInputPreview;
  const isRunningAi = isRunning && hasInputPreview;

  const runCheck = (runAi: boolean, language: TBreeamOutputLanguage = outputLanguage) => {
    if (!canStartCheck) {
      return;
    }

    void runBreeamCriterionCheck({
      ...breeamRunArgs,
      body: {
        body: {
          runAi,
          outputLanguage: language,
        },
      },
    });
  };

  const handleOutputLanguageChange = (value: string) => {
    const language = value as TBreeamOutputLanguage;
    setOutputLanguage(language);
  };

  useEffect(() => {
    if (!canStartCheck || hasLoadedInputPreviewRef.current) {
      return;
    }

    hasLoadedInputPreviewRef.current = true;
    runCheck(false);
  }, [canStartCheck]);

  useEffect(() => {
    if (hasAiResult) {
      setShowInputData(false);
    }
  }, [hasAiResult]);

  const reloadInputPreview = () => {
    resetBreeamCriterionCheck();
    runCheck(false);
  };

  const parsedAiAssessment = selectParsedAiAssessment(result);
  const effectiveVerdict = selectEffectiveVerdict({ parsedAiAssessment, result });
  const verdictColor = selectVerdictColor(effectiveVerdict);
  const technicalAnalysis = selectTechnicalAnalysis(result);
  const copyableAiAssessmentText = selectCopyableAiAssessmentText(result);
  const tokenEstimate = result?.inputData.aiInput.tokenEstimate;

  return (
    <Modal
      opened
      onClose={onClose}
      size="80vw"
      centered
      radius="lg"
      styles={{
        content: {
          maxWidth: "90rem",
          height: "88vh",
        },
        body: {
          height: "100%",
        },
      }}
      title={
        <Group gap="xs">
          <IconSparkles size={18} />
          <Text fw={600}>BREEAM-sjekk for sak {issueId}</Text>
        </Group>
      }
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600}>{issueTitle}</Text>
          </Stack>
        </Group>

        <Card
          withBorder
          radius="md"
          padding="sm"
          style={{
            backgroundColor: "var(--mantine-color-grape-0)",
            borderColor: "var(--mantine-color-grape-2)",
          }}
        >
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Text fw={600} size="sm">
                AI-kjøring
              </Text>
              <Text size="xs" c="dimmed">
                {tokenEstimate != null
                  ? `Estimert: ca. ${formatNumber(tokenEstimate.totalTokens)} tokens / ${formatNok(
                      tokenEstimate.estimatedCostNok,
                    )}`
                  : "Henter token- og kostnadsestimat..."}
              </Text>
            </Stack>

            <Group gap="sm">
              {tokenEstimate != null && (
                <Group gap={6}>
                  <Badge variant="light" color="blue">
                    ca. {formatNumber(tokenEstimate.totalTokens)} tokens
                  </Badge>
                  <Badge variant="light" color="grape">
                    ca. {formatNok(tokenEstimate.estimatedCostNok)}
                  </Badge>
                </Group>
              )}
              <SegmentedControl
                value={outputLanguage}
                onChange={handleOutputLanguageChange}
                data={[
                  { label: "Norsk", value: "NO" },
                  { label: "English", value: "EN" },
                ]}
              />
              <Button
                variant={hasAiResult ? "light" : "filled"}
                leftSection={hasAiResult ? <IconRefresh size={16} /> : <IconSparkles size={16} />}
                loading={isRunningAi}
                disabled={!canStartCheck || !hasInputPreview}
                onClick={() => runCheck(true)}
              >
                {hasAiResult ? "Kjør AI på nytt" : "Kjør AI"}
              </Button>
            </Group>
          </Group>
        </Card>

        {isCheckingPrerequisites && (
          <Alert color="blue" variant="light" icon={<IconSparkles size={18} />} title="Sjekker underlag">
            <Text size="sm">Kontrollerer at saken har PDF-filer og veiledningstekst før analysen starter.</Text>
          </Alert>
        )}

        {isCheckingPrerequisites === false && missingPrerequisites.length > 0 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertCircle size={18} />}
            title="BREEAM-sjekken kan ikke kjøres"
          >
            <Stack gap={4}>
              {missingPrerequisites.map((message) => (
                <Text key={message} size="sm">
                  {message}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {isRunning && (
          <Card withBorder radius="md" padding="lg">
            <Stack gap="md" align="center">
              <Loader size="xl" color="blue" />
              <Alert
                w="100%"
                color="blue"
                variant="light"
                icon={<IconSparkles size={18} className="breeam-loading-icon" />}
                title={isLoadingInputPreview ? "Henter AI-grunnlag" : "Kjører AI-analyse"}
              >
                <Stack gap={6}>
                  <Text size="sm">
                    {isLoadingInputPreview
                      ? "Henter PDF-tekst, velger relevante utdrag og bygger prompten før AI kan kjøres."
                      : "Modellen leser underlag, vurderer kriteriet og bygger opp et svar med evidens."}
                  </Text>
                  <Text size="sm" c="dimmed" className="breeam-loading-dots">
                    Dette kan ta noen sekunder
                  </Text>
                </Stack>
              </Alert>

              <Group gap="xs">
                <Badge variant="light" color="blue" className="breeam-loading-badge">
                  Dokumenter
                </Badge>
                <Badge
                  variant="light"
                  color="cyan"
                  className="breeam-loading-badge"
                  style={{ animationDelay: "120ms" }}
                >
                  Analyse
                </Badge>
                <Badge
                  variant="light"
                  color="teal"
                  className="breeam-loading-badge"
                  style={{ animationDelay: "240ms" }}
                >
                  Evidens
                </Badge>
              </Group>

              <style>
                {`
                  .breeam-loading-icon {
                    animation: breeamPulse 1.4s ease-in-out infinite;
                  }

                  .breeam-loading-dots::after {
                    content: "...";
                    display: inline-block;
                    width: 1.2em;
                    overflow: hidden;
                    vertical-align: bottom;
                    animation: breeamDots 1.2s steps(4, end) infinite;
                  }

                  .breeam-loading-badge {
                    animation: breeamFloat 1.8s ease-in-out infinite;
                  }

                  @keyframes breeamPulse {
                    0%, 100% { transform: scale(1); opacity: 0.75; }
                    50% { transform: scale(1.08); opacity: 1; }
                  }

                  @keyframes breeamDots {
                    0% { width: 0; }
                    100% { width: 1.2em; }
                  }

                  @keyframes breeamFloat {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                  }
                `}
              </style>
            </Stack>
          </Card>
        )}

        {runError != null && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={18} />}
            title="Kunne ikke kjøre BREEAM-sjekken"
          >
            <Text size="sm">{getErrorMessage(runError)}</Text>
          </Alert>
        )}

        {result != null && (
          <ScrollArea.Autosize mah="74vh" offsetScrollbars>
            <Stack gap="md">
              <SectionCard title="Oppsummering">
                <Group justify="space-between" align="center">
                  <Text>{hasAiResult ? parsedAiAssessment.summary || result.summary : result.summary}</Text>
                  {hasAiResult ? (
                    <Badge color={verdictColor} variant="light" size="lg">
                      {effectiveVerdict}
                    </Badge>
                  ) : (
                    <Badge color="blue" variant="light" size="lg">
                      Inndata klar
                    </Badge>
                  )}
                </Group>
              </SectionCard>

              <Card withBorder radius="md" padding="md">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600}>Indata</Text>
                    <Button variant="subtle" color="gray" size="xs" onClick={() => setShowInputData((current) => !current)}>
                      {showInputData ? "Skjul inndata" : "Vis inndata"}
                    </Button>
                  </Group>

                  {showInputData && (
                    <Stack gap="sm">
                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        {guidanceTextSource.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {guidanceTextSource.description}
                      </Text>
                      <Divider />
                      <Text size="sm">
                        {result.inputData.otherAttributeIText?.trim() || "Ingen verdi satt på attributt I."}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">
                          PDF-underlag
                        </Text>
                        <Badge variant="light" color="blue">
                          {result.inputData.pdfFileCount} PDF
                        </Badge>
                      </Group>
                      <Divider />
                      {result.inputData.pdfFiles.length > 0 ? (
                        <Stack gap="xs">
                          {result.inputData.pdfFiles.map((file, index) => (
                            <Text key={`${file.blobName}-${index}`} size="sm">
                              {index + 1}. {file.fileName} ({file.extractedTextLength} tegn)
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm">Ingen PDF-filer funnet for denne saken.</Text>
                      )}
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">
                          Søkord brukt for PDF-utvalg
                        </Text>
                        <Badge variant="light" color="blue">
                          {result.inputData.aiInput.relevanceKeywords.length} søkord
                        </Badge>
                      </Group>
                      <Divider />
                      <Text size="sm">{result.inputData.aiInput.relevanceKeywords.join(", ")}</Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">
                          PDF-tekst sendt til AI
                        </Text>
                        <Button variant="subtle" color="gray" size="xs" onClick={reloadInputPreview}>
                          Hent inndata på nytt
                        </Button>
                      </Group>
                      <Divider />
                      <Accordion variant="separated" multiple defaultValue={[]}>
                        {result.inputData.aiInput.documents.map((document) => (
                          <Accordion.Item key={document.fileName} value={document.fileName}>
                            <Accordion.Control>
                              <Group justify="space-between" align="center" pr="md">
                                <Stack gap={2}>
                                  <Text fw={600} size="sm">
                                    {document.fileName}
                                  </Text>
                                  {document.pages.length > 0 && (
                                    <Text size="xs" c="dimmed">
                                      Valgte sider:{" "}
                                      {document.pages
                                        .filter((page) => page.selected)
                                        .map((page) => page.pageNumber)
                                        .join(", ") || "Ingen"}
                                    </Text>
                                  )}
                                </Stack>
                                <Badge variant="light" color="teal">
                                  {document.selectedTextLength} / {document.totalTextLength} tegn
                                </Badge>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
                                {document.selectedText || "Ingen tekst valgt fra dokumentet."}
                              </Text>
                            </Accordion.Panel>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text fw={600} size="sm">
                          Prompt som sendes til AI
                        </Text>
                        <CopyButton value={result.inputData.aiInput.prompt}>
                          {({ copied, copy }) => (
                            <Button
                              variant="subtle"
                              color={copied ? "teal" : "blue"}
                              size="xs"
                              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                              onClick={copy}
                            >
                              {copied ? "Prompt kopiert" : "Kopier prompt"}
                            </Button>
                          )}
                        </CopyButton>
                      </Group>
                      <Divider />
                      <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
                        {result.inputData.aiInput.prompt}
                      </Text>
                    </Stack>
                  </Card>
                    </Stack>
                  )}
                </Stack>
              </Card>

              {hasAiResult && (
                <SectionCard title="Analyse">
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Badge color={verdictColor} variant="light" size="lg">
                      {effectiveVerdict}
                    </Badge>

                    <CopyButton value={copyableAiAssessmentText}>
                      {({ copied, copy }) => (
                        <Button
                          variant={copied ? "filled" : "light"}
                          color={copied ? "teal" : "blue"}
                          size="sm"
                          leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          onClick={copy}
                        >
                          {copied ? "Tekst kopiert" : "Kopier tekst til BRE"}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Tekst til BRE
                      </Text>
                      <Divider />
                      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                        {parsedAiAssessment.breText || "Ingen tekst til BRE funnet i AI-svaret."}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Kort vurdering
                      </Text>
                      <Divider />
                      <Text size="sm">{parsedAiAssessment.summary || "Ingen kort vurdering funnet i AI-svaret."}</Text>
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Dokumentasjon brukt
                      </Text>
                      <Divider />
                      {parsedAiAssessment.supportingPoints.length > 0 ? (
                        <Stack gap="xs">
                          {parsedAiAssessment.supportingPoints.map((point, index) => (
                            <Text key={`${index}-${point.slice(0, 20)}`} size="sm">
                              • {point}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Ingen dokumentasjonspunkter funnet i AI-svaret.
                        </Text>
                      )}
                    </Stack>
                  </Card>

                  <Card withBorder radius="md" padding="sm">
                    <Stack gap={6}>
                      <Text fw={600} size="sm">
                        Gap / usikkerheter
                      </Text>
                      <Divider />
                      {parsedAiAssessment.gaps.length > 0 ? (
                        <Stack gap="xs">
                          {parsedAiAssessment.gaps.map((gap, index) => (
                            <Text key={`${index}-${gap.slice(0, 20)}`} size="sm">
                              • {gap}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Ingen gap funnet i AI-svaret.
                        </Text>
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </SectionCard>
              )}

              {technicalAnalysis.length > 0 && (
                <Stack gap="xs">
                  <Group justify="flex-end">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setShowTechnicalDebug((current) => !current)}
                    >
                      {showTechnicalDebug ? "Skjul teknisk debug" : "Vis teknisk debug"}
                    </Button>
                  </Group>

                  {showTechnicalDebug && (
                    <SectionCard title="Teknisk debug">
                      <Stack gap="xs">
                        {technicalAnalysis.map((entry, index) => (
                          <Text key={`${index}-${entry.slice(0, 20)}`} size="sm">
                            {index + 1}. {entry}
                          </Text>
                        ))}
                      </Stack>
                    </SectionCard>
                  )}
                </Stack>
              )}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </Modal>
  );
};

export default IssueBreeamCriterionCheckModal;
